import {
  hashKey,
  notifyManager,
  type Query,
  type QueryCacheNotifyEvent,
  type QueryClient,
} from '@tanstack/react-query';
import { isTerminalMarket, projectRowConviction } from '../helpers/conviction.js';
import { createConvictionProjection } from '../helpers/conviction-projection.js';
import type { AppStateSlot } from '../platform/adapter.js';
import { hashDiagnosticEntity } from '../tx-engine/diagnostics/lifecycle.js';
import { sdkEvents } from './events.js';
import {
  hasConvictionProjectionInterest,
  onConvictionProjectionInterestChanged,
} from './internal/conviction-projection-interest.js';
import {
  isConvictionProjectionWrite,
  writeConvictionProjection,
} from './internal/conviction-projection-write.js';
import {
  parseMarketConfig,
  readMarketConfig,
  requestMarketConfig,
} from './internal/market-config-catalog.js';

export interface ConvictionProjectorOptions {
  readonly intervalMs?: number;
  readonly nowMs?: () => bigint;
  readonly nowSecs?: () => bigint;
  readonly isRowActive?: (row: unknown) => boolean;
  readonly appState?: AppStateSlot;
  /** SDKProvider dispatches cache events through its single ordered listener. */
  readonly managedListener?: boolean;
}

type Projection = { signature: string; at: (nowMs: bigint) => bigint };

/** One provider clock, one write per canonical outcome, independent of consumer count. */
export function installConvictionProjector(qc: QueryClient, opts: ConvictionProjectorOptions = {}) {
  const cache = qc.getQueryCache();
  const candidates = new Map<string, Query>();
  const projections = new Map<string, Projection>();
  const diagnosed = new Set<string>();
  const nowMs =
    opts.nowMs ??
    (opts.nowSecs === undefined
      ? () => BigInt(Date.now())
      : () => (opts.nowSecs?.() ?? 0n) * 1000n);
  let active = opts.appState?.current() !== 'background' && opts.appState?.current() !== 'inactive';
  let disposed = false;
  let queued = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  function index(query: Query): void {
    if (query.queryKey[0] !== 'markets') return;
    const kind = query.queryKey[1];
    if (kind !== 'entity' && kind !== 'detail' && kind !== 'list') return;
    if (kind !== 'entity' && !isLegacyMarketPayload(query.state.data)) {
      candidates.delete(query.queryHash);
      return;
    }
    const id = record(query.state.data)?.pubkey;
    if (
      query.getObserversCount() > 0 ||
      (typeof id === 'string' && hasConvictionProjectionInterest(qc, id))
    )
      candidates.set(query.queryHash, query);
    else candidates.delete(query.queryHash);
  }

  function diagnose(query: Query, reason: string): void {
    if (diagnosed.has(query.queryHash)) return;
    diagnosed.add(query.queryHash);
    sdkEvents.emit('conviction-projector:diagnostic', {
      entityHash: hashDiagnosticEntity(query.queryHash),
      reason,
    });
  }

  function projectMarket(query: Query, now: bigint): void {
    const market = record(query.state.data);
    if (market === undefined || isTerminalMarket(market)) return;
    const id = market.pubkey;
    const interested = typeof id === 'string' && hasConvictionProjectionInterest(qc, id);
    if (!interested && (query.getObserversCount() === 0 || opts.isRowActive?.(market) === false))
      return;
    if (!Array.isArray(market.outcomes)) return;
    const updatedAt = integer(market.convictionUpdatedAt);
    const retention = retentionFor(query, market);
    if (retention === undefined) return;
    if (updatedAt === undefined || retention === undefined || retention > 1_000_000_000n) {
      diagnose(query, 'invalid-market-snapshot');
      return;
    }
    for (const reference of market.outcomes) {
      const ref = record(reference);
      if (ref === undefined || typeof ref.mint !== 'string') continue;
      const outcomeQuery = cache.get(hashKey(['outcomes', 'entity', ref.mint]));
      const outcome = record(outcomeQuery?.state.data);
      if (outcomeQuery === undefined || outcome === undefined) continue;
      if (outcome.market !== id || outcome.outcomeIndex !== ref.outcomeIndex) {
        diagnose(outcomeQuery, 'outcome-identity-mismatch');
        continue;
      }
      const anchor = integer(outcome.convictionAnchor);
      const target = integer(outcome.convictionTarget);
      if (anchor === undefined || target === undefined) {
        diagnose(outcomeQuery, 'invalid-outcome-snapshot');
        continue;
      }
      const signature = `${anchor}:${target}:${updatedAt}:${retention}`;
      let projection = projections.get(outcomeQuery.queryHash);
      if (projection?.signature !== signature) {
        projection = {
          signature,
          at: createConvictionProjection({
            anchorPpb: anchor,
            targetPpb: target,
            updatedAtSecs: updatedAt,
            retentionPerSecondPpb: retention,
          }),
        };
        projections.set(outcomeQuery.queryHash, projection);
      }
      const conviction = projection.at(now).toString();
      if (conviction !== outcome.conviction)
        writeConvictionProjection(qc, outcomeQuery, { ...outcome, conviction });
    }
  }

  function retentionFor(query: Query, market: Record<string, unknown>): bigint | undefined {
    const address = typeof market.marketConfig === 'string' ? market.marketConfig : undefined;
    const config = readMarketConfig(qc, address);
    if (config === undefined) {
      diagnose(query, 'missing-market-config');
      if (address !== undefined) requestMarketConfig(qc, address);
      return undefined;
    }
    try {
      const retention = parseMarketConfig(config).convictionRetentionPerSecondPpb;
      if (retention < 0n || retention >= 1_000_000_000n) {
        diagnose(query, 'invalid-market-config');
        return undefined;
      }
      diagnosed.delete(query.queryHash);
      return retention;
    } catch {
      diagnose(query, 'invalid-market-config');
      return undefined;
    }
  }

  function tick(): void {
    if (!active || disposed) return;
    const now = nowMs();
    notifyManager.batch(() => {
      for (const query of candidates.values()) {
        if (query.queryKey[1] === 'entity') projectMarket(query, now);
        else projectLegacy(query, now);
      }
    });
  }

  function projectLegacy(query: Query, now: bigint): void {
    if (query.getObserversCount() === 0) return;
    const data = record(query.state.data);
    if (data === undefined) return;
    const project = (row: unknown): unknown => {
      const value = record(row);
      if (value === undefined || isTerminalMarket(value) || opts.isRowActive?.(row) === false)
        return row;
      const retention = retentionFor(query, value);
      return retention === undefined ? row : projectRowConviction(value, now / 1000n, retention);
    };
    const rows = Array.isArray(data.data) ? data.data.map(project) : undefined;
    const changed =
      rows === undefined
        ? project(data)
        : rows.some((row, i) => row !== (data.data as unknown[])[i])
          ? { ...data, data: rows }
          : data;
    if (changed !== data) writeConvictionProjection(qc, query, changed);
  }

  function onCacheEvent(event: QueryCacheNotifyEvent): void {
    if (isConvictionProjectionWrite(qc, event.query.queryHash)) return;
    if (event.type === 'removed') {
      candidates.delete(event.query.queryHash);
      projections.delete(event.query.queryHash);
      diagnosed.delete(event.query.queryHash);
      return;
    }
    if (
      event.type === 'added' ||
      event.type === 'updated' ||
      event.type === 'observerAdded' ||
      event.type === 'observerRemoved'
    )
      index(event.query);
    if (event.type !== 'added' && event.type !== 'updated' && event.type !== 'observerAdded')
      return;
    if (
      event.query.queryKey[0] !== 'markets' &&
      event.query.queryKey[0] !== 'outcomes' &&
      event.query.queryKey[0] !== 'market-configs'
    )
      return;
    if (queued) return;
    queued = true;
    // Normalization writes parent and outcomes synchronously; join the complete batch.
    queueMicrotask(() => {
      queued = false;
      tick();
    });
  }

  function start(): void {
    if (!active || timer !== undefined) return;
    tick();
    timer = setInterval(tick, opts.intervalMs ?? 100);
  }

  for (const query of cache.getAll()) index(query);
  const unsubscribeInterest = onConvictionProjectionInterestChanged(qc, (id) => {
    const query = cache.get(hashKey(['markets', 'entity', id]));
    if (query !== undefined) {
      index(query);
      tick();
    }
  });
  const unsubscribe = opts.managedListener === true ? undefined : cache.subscribe(onCacheEvent);
  const unsubscribeState = opts.appState?.addListener((event) => {
    active = event.kind !== 'hidden' && event.kind !== 'freeze';
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
    start();
  });
  start();
  return Object.assign(
    () => {
      disposed = true;
      if (timer !== undefined) clearInterval(timer);
      unsubscribe?.();
      unsubscribeInterest();
      unsubscribeState?.();
      candidates.clear();
      projections.clear();
      diagnosed.clear();
    },
    { onCacheEvent },
  );
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function integer(value: unknown): bigint | undefined {
  return typeof value === 'string' && /^\d+$/.test(value) ? BigInt(value) : undefined;
}

function isLegacyMarketPayload(value: unknown): boolean {
  const data = record(value);
  const row = Array.isArray(data?.data) ? record(data.data[0]) : data;
  return (
    typeof row?.pubkey === 'string' &&
    (Array.isArray(row.outcomes) || Array.isArray(row.outcomesLite))
  );
}
